import { ornament } from '@/gfx/ornaments';
import { finishMaterial } from '@/gfx/materials';
import { furnitureSprite } from '@/gfx/furniture';
import { Painter } from '@/gfx/pixel';
import { C } from '@/gfx/palette';
import type { DecorKind } from './types';

export interface HideSpec { cap: number; label: string; verb: string }
export interface PropLight { r: number; color: string; intensity?: number; flicker?: boolean; powered?: boolean; ox: number; oy: number }

export interface PropKind {
  id: string;
  name: string;
  fw: number; fh: number; // footprint (tiles)
  sw: number; sh: number; // sprite size (px)
  tall: boolean; // blocks light
  solid: boolean;
  flat?: boolean; // drawn under everything, no collision
  hide?: HideSpec;
  search?: boolean;
  push?: boolean;
  light?: PropLight;
  interact?: 'safe' | 'fusebox' | 'piano' | 'lever' | 'well' | 'nest' | 'organ' | 'toybox' | 'clock' | 'telescope' | 'mirror' | 'bedNote';
  draw: () => HTMLCanvasElement;
  /** category for the auto-furnisher */
  wallHug?: boolean; // prefers being placed against the north wall
}

const O = C.outline;
const W = { base: C.panel, dk: C.panelDk, lt: C.panelLt };
const PW = { base: C.woodPale, dk: C.woodPaleDk, lt: '#cfa876' };

function woodBox(p: Painter, x: number, y: number, w: number, h: number, wood = W) {
  p.panel(x, y, w, h, wood.base, O, wood.lt, wood.dk);
  if (w > 10 && h > 6) {
    p.hline(x + 3, y + 2, w - 6, wood.lt);
    for (let row = 5; row < h - 2; row += 6) {
      p.hline(x + 3, y + row, Math.max(2, w / 3 | 0), wood.dk);
      p.hline(x + Math.floor(w * 0.6), y + row + 1, Math.max(2, w / 5 | 0), wood.lt);
    }
    p.px(x + 2, y + h - 3, C.brassDk).px(x + w - 3, y + h - 3, C.brassDk);
  }
}
function feet(p: Painter, x: number, y: number, w: number, col = O) {
  p.rect(x + 1, y, 2, 2, col).rect(x + w - 3, y, 2, 2, col);
}

/**
 * Where a prop's light actually shines from. The glow is painted high on the sprite (a candle
 * flame, a fire), which can sit visually inside the wall behind it; the light itself has to stay
 * on the prop's own floor tiles or it gets trapped behind the wall's blocker.
 */
export function propLightPos(kind: PropKind, tx: number, ty: number): [number, number] {
  const L = kind.light!;
  const x0 = tx * 16, y0 = ty * 16;
  const x1 = (tx + kind.fw) * 16, y1 = (ty + kind.fh) * 16;
  const x = Math.min(Math.max(x0 + L.ox, x0 + 2), x1 - 2);
  const y = Math.min(Math.max(y1 - kind.sh + L.oy, y0 + 2), y1 - 2);
  return [x, y];
}

const cache = new Map<string, HTMLCanvasElement>();
export function propSprite(kind: PropKind): HTMLCanvasElement {
  let c = cache.get(kind.id);
  if (!c) {
    c = finishMaterial(furnitureSprite(kind.id) ?? kind.draw());
    cache.set(kind.id, c);
  }
  return c;
}

function def(k: Omit<PropKind, 'tall' | 'solid'> & { tall?: boolean; solid?: boolean }): PropKind {
  return { tall: false, solid: true, ...k };
}

export const PROPS: Record<string, PropKind> = {};
function reg(k: PropKind) {
  PROPS[k.id] = k;
}

// ---------- beds ----------
reg(def({
  id: 'bed', name: '침대', fw: 2, fh: 3, sw: 32, sh: 48, hide: { cap: 2, label: '침대 밑', verb: '침대 밑으로 숨는다' }, wallHug: true, interact: 'bedNote',
  draw: () => {
    const p = new Painter(32, 48);
    woodBox(p, 0, 0, 32, 10); // headboard
    p.hline(3, 3, 26, W.lt);
    p.panel(1, 8, 30, 38, C.sheet, O, C.pillow, C.sheetSh); // mattress
    p.panel(4, 11, 24, 8, C.pillow, C.sheetSh); // pillow
    p.panel(1, 20, 30, 24, C.fabricRed, O, '#c04a4a', C.fabricRedDk); // blanket
    p.hline(2, 22, 28, '#c04a4a');
    for (let i = 0; i < 4; i++) p.hline(3 + i * 7, 30, 4, C.fabricRedDk);
    p.rect(1, 44, 30, 3, '#0e0a12'); // under-bed gap
    p.hline(0, 47, 32, O);
    feet(p, 0, 45, 32);
    return p.done();
  },
}));
reg(def({
  id: 'bedSmall', name: '작은 침대', fw: 1, fh: 2, sw: 16, sh: 32, hide: { cap: 1, label: '침대 밑', verb: '침대 밑으로 숨는다' }, wallHug: true,
  draw: () => {
    const p = new Painter(16, 32);
    woodBox(p, 0, 0, 16, 7);
    p.panel(0, 5, 16, 24, C.sheet, O, C.pillow, C.sheetSh);
    p.panel(2, 7, 12, 5, C.pillow, C.sheetSh);
    p.panel(0, 13, 16, 16, C.fabricBlue, O, '#4a6aa8', C.fabricBlueDk);
    p.rect(1, 29, 14, 2, '#0e0a12');
    p.hline(0, 31, 16, O);
    return p.done();
  },
}));
reg(def({
  id: 'cot', name: '병상', fw: 1, fh: 2, sw: 16, sh: 32, hide: { cap: 1, label: '병상 밑', verb: '병상 밑으로 숨는다' }, wallHug: true,
  draw: () => {
    const p = new Painter(16, 32);
    p.panel(0, 0, 16, 6, C.metal, O, C.metalLt, C.metalDk);
    p.panel(0, 4, 16, 25, C.sheet, O, C.pillow, C.sheetSh);
    p.panel(2, 6, 12, 5, C.pillow, C.sheetSh);
    p.panel(0, 12, 16, 17, '#a9b7a6', O, '#c1cdbf', '#7f8f7c');
    p.rect(1, 29, 14, 2, '#0e0a12');
    p.hline(0, 31, 16, O);
    return p.done();
  },
}));

// ---------- tall storage ----------
reg(def({
  id: 'wardrobe', name: '옷장', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, hide: { cap: 1, label: '옷장 안', verb: '옷장 안으로 숨는다' }, wallHug: true,
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 2, 32, 36);
    p.rect(0, 0, 32, 3, W.lt).hline(0, 0, 32, O).hline(0, 3, 32, W.dk);
    p.vline(15, 4, 32, W.dk).vline(16, 4, 32, O);
    p.box(3, 6, 10, 28, W.dk).box(19, 6, 10, 28, W.dk);
    p.rect(13, 20, 2, 3, C.brass).rect(17, 20, 2, 3, C.brass);
    p.hline(0, 38, 32, O);
    feet(p, 0, 38, 32);
    return p.done();
  },
}));
reg(def({
  id: 'wardrobePush', name: '무거운 옷장', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, push: true, wallHug: true,
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 2, 32, 36, { base: '#4a2f1d', dk: '#33200f', lt: '#644127' });
    p.rect(0, 0, 32, 3, '#644127').hline(0, 0, 32, O).hline(0, 3, 32, '#33200f');
    p.vline(15, 4, 32, '#33200f').vline(16, 4, 32, O);
    p.box(3, 6, 10, 28, '#33200f').box(19, 6, 10, 28, '#33200f');
    p.rect(13, 20, 2, 3, C.metal).rect(17, 20, 2, 3, C.metal);
    p.hline(0, 38, 32, O);
    p.noise(2, 5, 28, 30, '#3a2718', 0.08, 4);
    feet(p, 0, 38, 32);
    return p.done();
  },
}));
const BOOKS = ['#a33a3a', '#3a5a9a', '#4f9d5a', '#c9a24a', '#8a5bb8', '#e07b2c', '#d9d6dc', '#3aa1a1'];
function shelfBooks(p: Painter, x: number, y: number, w: number, seed: number) {
  let s = seed;
  let cx = x;
  while (cx < x + w - 1) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const bw = 1 + (s >>> 8) % 3;
    const col = BOOKS[(s >>> 12) % BOOKS.length];
    const bh = 6 + ((s >>> 16) % 3);
    if (cx + bw > x + w) break;
    p.rect(cx, y + (8 - bh), bw, bh, col);
    cx += bw + ((s >>> 20) % 2 === 0 ? 0 : 1);
  }
}
reg(def({
  id: 'bookshelf', name: '책장', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, wallHug: true, search: true,
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 0, 32, 40);
    for (let i = 0; i < 3; i++) {
      p.rect(2, 3 + i * 11, 28, 9, '#2a1a10');
      shelfBooks(p, 3, 4 + i * 11, 26, 17 + i * 5);
      p.hline(2, 12 + i * 11, 28, W.lt);
    }
    p.hline(0, 39, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'bookshelfLever', name: '책장', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, wallHug: true, interact: 'lever',
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 0, 32, 40);
    for (let i = 0; i < 3; i++) {
      p.rect(2, 3 + i * 11, 28, 9, '#2a1a10');
      shelfBooks(p, 3, 4 + i * 11, 26, 31 + i * 3);
      p.hline(2, 12 + i * 11, 28, W.lt);
    }
    p.hline(0, 39, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'bookshelfSecret', name: '책장', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, wallHug: true, push: true,
  draw: () => propSprite(PROPS.bookshelf),
}));
reg(def({
  id: 'cabinet', name: '진열장', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, wallHug: true, search: true,
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 0, 32, 40);
    p.panel(3, 3, 26, 20, C.glass, O, C.glassLt, '#6f95a8');
    p.rect(5, 8, 3, 5, '#c9a24a').rect(10, 9, 4, 4, '#d9d6dc').rect(18, 7, 3, 6, '#8a5bb8').rect(23, 10, 4, 3, '#4f9d5a');
    p.hline(3, 15, 26, W.lt);
    p.box(3, 25, 12, 11, W.dk).box(17, 25, 12, 11, W.dk);
    p.rect(8, 30, 2, 2, C.brass).rect(22, 30, 2, 2, C.brass);
    p.hline(0, 39, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'shelf', name: '선반', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, wallHug: true, search: true,
  draw: () => {
    const p = new Painter(32, 40);
    p.panel(0, 0, 32, 40, '#4b3a2e', O, '#5f4a3b', '#3a2c22');
    for (let i = 0; i < 3; i++) {
      p.rect(2, 3 + i * 12, 28, 10, '#231a14');
      p.hline(2, 13 + i * 12, 28, '#6b5343');
      const y = 5 + i * 12;
      p.rect(4, y + 2, 4, 6, '#7a8f5a').rect(10, y + 1, 3, 7, '#c9a24a').rect(15, y + 3, 5, 5, '#9a6a4a').rect(23, y, 4, 8, '#8fb4c8');
    }
    p.hline(0, 39, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'wineRack', name: '와인 선반', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, wallHug: true, search: true,
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 0, 32, 40, { base: '#4a3324', dk: '#33221a', lt: '#5f4230' });
    for (let j = 0; j < 4; j++)
      for (let i = 0; i < 4; i++) {
        const x = 3 + i * 7, y = 3 + j * 9;
        p.rect(x, y, 5, 7, '#231a14');
        if ((i + j) % 3 !== 0) p.rect(x + 1, y + 1, 3, 3, '#3b7a4a').px(x + 2, y + 2, '#5aa066');
      }
    p.hline(0, 39, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'dollShelf', name: '인형 선반', fw: 2, fh: 1, sw: 32, sh: 40, tall: true, wallHug: true,
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 0, 32, 40, PW);
    const heads = ['#f7d9b9', '#e0c9a8', '#f7d9b9', '#d7c0a0'];
    const dress = ['#e07aa8', '#3b6fc7', '#f4f0e6', '#8a5bb8'];
    for (let j = 0; j < 3; j++) {
      p.rect(2, 3 + j * 12, 28, 10, '#3b2a1e');
      for (let i = 0; i < 4; i++) {
        const x = 3 + i * 7, y = 4 + j * 12;
        p.rect(x + 1, y, 4, 4, heads[(i + j) % 4]);
        p.px(x + 2, y + 1, O).px(x + 4, y + 1, O);
        p.rect(x, y + 4, 6, 5, dress[(i * 3 + j) % 4]);
      }
      p.hline(2, 13 + j * 12, 28, PW.lt);
    }
    p.hline(0, 39, 32, O);
    return p.done();
  },
}));

// ---------- seating ----------
reg(def({
  id: 'sofa', name: '쇼파', fw: 3, fh: 1, sw: 48, sh: 24, hide: { cap: 1, label: '쇼파 뒤', verb: '쇼파 뒤로 숨는다' },
  draw: () => {
    const p = new Painter(48, 24);
    p.panel(0, 0, 48, 10, C.fabricGreen, O, '#5a9a68', C.fabricGreenDk); // back
    p.panel(0, 8, 6, 14, C.fabricGreen, O, '#5a9a68', C.fabricGreenDk);
    p.panel(42, 8, 6, 14, C.fabricGreen, O, '#5a9a68', C.fabricGreenDk);
    p.panel(5, 9, 38, 12, '#4a8a5c', O, '#5a9a68', C.fabricGreenDk);
    p.vline(17, 10, 10, C.fabricGreenDk).vline(30, 10, 10, C.fabricGreenDk);
    p.hline(0, 21, 48, O);
    feet(p, 0, 22, 48);
    return p.done();
  },
}));
reg(def({
  id: 'sofaRed', name: '쇼파', fw: 3, fh: 1, sw: 48, sh: 24, hide: { cap: 1, label: '쇼파 뒤', verb: '쇼파 뒤로 숨는다' },
  draw: () => {
    const p = new Painter(48, 24);
    p.panel(0, 0, 48, 10, C.fabricRed, O, '#c05050', C.fabricRedDk);
    p.panel(0, 8, 6, 14, C.fabricRed, O, '#c05050', C.fabricRedDk);
    p.panel(42, 8, 6, 14, C.fabricRed, O, '#c05050', C.fabricRedDk);
    p.panel(5, 9, 38, 12, '#8f3232', O, '#c05050', C.fabricRedDk);
    p.vline(17, 10, 10, C.fabricRedDk).vline(30, 10, 10, C.fabricRedDk);
    p.hline(0, 21, 48, O);
    feet(p, 0, 22, 48);
    return p.done();
  },
}));
reg(def({
  id: 'armchair', name: '안락의자', fw: 1, fh: 1, sw: 16, sh: 20,
  draw: () => {
    const p = new Painter(16, 20);
    p.panel(0, 0, 16, 9, C.fabricBlue, O, '#4a6aa8', C.fabricBlueDk);
    p.panel(0, 7, 4, 11, C.fabricBlue, O, '#4a6aa8', C.fabricBlueDk);
    p.panel(12, 7, 4, 11, C.fabricBlue, O, '#4a6aa8', C.fabricBlueDk);
    p.panel(3, 8, 10, 9, '#2f4a80', O, '#4a6aa8', C.fabricBlueDk);
    p.hline(0, 17, 16, O);
    feet(p, 0, 18, 16);
    return p.done();
  },
}));
reg(def({
  id: 'chair', name: '의자', fw: 1, fh: 1, sw: 16, sh: 18,
  draw: () => {
    const p = new Painter(16, 18);
    woodBox(p, 3, 0, 10, 8);
    p.vline(6, 2, 4, W.dk).vline(9, 2, 4, W.dk);
    woodBox(p, 2, 7, 12, 7);
    p.rect(3, 14, 2, 3, O).rect(11, 14, 2, 3, O);
    p.hline(0, 17, 16, 'rgba(0,0,0,0)');
    return p.done();
  },
}));
reg(def({
  id: 'bench', name: '긴 의자', fw: 2, fh: 1, sw: 32, sh: 20,
  draw: () => {
    const p = new Painter(32, 20);
    woodBox(p, 0, 0, 32, 6);
    woodBox(p, 0, 7, 32, 8);
    p.rect(2, 15, 2, 4, O).rect(28, 15, 2, 4, O);
    return p.done();
  },
}));
reg(def({
  id: 'pew', name: '예배 의자', fw: 3, fh: 1, sw: 48, sh: 20,
  draw: () => {
    const p = new Painter(48, 20);
    woodBox(p, 0, 0, 48, 8, { base: '#4a3324', dk: '#33221a', lt: '#5f4230' });
    woodBox(p, 0, 8, 48, 8, { base: '#4a3324', dk: '#33221a', lt: '#5f4230' });
    p.rect(2, 16, 2, 4, O).rect(44, 16, 2, 4, O).rect(23, 16, 2, 4, O);
    return p.done();
  },
}));

// ---------- tables ----------
reg(def({
  id: 'table', name: '탁자', fw: 2, fh: 2, sw: 32, sh: 32,
  draw: () => {
    const p = new Painter(32, 32);
    p.rect(3, 20, 3, 10, W.dk).rect(26, 20, 3, 10, W.dk).rect(3, 29, 3, 2, O).rect(26, 29, 3, 2, O);
    woodBox(p, 0, 4, 32, 18);
    p.hline(2, 6, 28, W.lt);
    p.rect(12, 8, 8, 6, C.paper).box(12, 8, 8, 6, C.paperDk);
    return p.done();
  },
}));
reg(def({
  id: 'tableRound', name: '원탁', fw: 2, fh: 2, sw: 32, sh: 30,
  draw: () => {
    const p = new Painter(32, 30);
    p.rect(14, 16, 4, 12, W.dk).rect(10, 27, 12, 3, O).rect(11, 27, 10, 2, W.dk);
    p.art(0, 0, [
      '......OOOOOOOOOOOOOOOOOOOO......',
      '...OOOLLLLLLLLLLLLLLLLLLLLOOO...',
      '.OOLLLWWWWWWWWWWWWWWWWWWWWLLLOO.',
      'OLLWWWWWWWWWWWWWWWWWWWWWWWWWWLLO',
      'OLWWWWWWWWWWWWWWWWWWWWWWWWWWWWLO',
      'OLWWWWWWWWWWWWWWWWWWWWWWWWWWWWLO',
      'OLLWWWWWWWWWWWWWWWWWWWWWWWWWWLLO',
      '.OOLLLWWWWWWWWWWWWWWWWWWWWLLLOO.',
      '...OOODDDDDDDDDDDDDDDDDDDDOOO...',
      '......OOOOOOOOOOOOOOOOOOOO......',
    ], { O, L: W.lt, W: W.base, D: W.dk });
    return p.done();
  },
}));
reg(def({
  id: 'tableLong', name: '긴 식탁', fw: 4, fh: 2, sw: 64, sh: 32,
  draw: () => {
    const p = new Painter(64, 32);
    p.rect(3, 22, 3, 8, W.dk).rect(58, 22, 3, 8, W.dk).rect(3, 29, 3, 2, O).rect(58, 29, 3, 2, O);
    woodBox(p, 0, 6, 64, 18);
    p.panel(2, 2, 60, 18, C.sheet, O, C.pillow, C.sheetSh);
    // plates and candles
    for (let i = 0; i < 4; i++) {
      p.rect(6 + i * 15, 6, 6, 4, '#d9d6dc').box(6 + i * 15, 6, 6, 4, '#9a9aa4');
      p.rect(6 + i * 15, 13, 6, 4, '#d9d6dc').box(6 + i * 15, 13, 6, 4, '#9a9aa4');
    }
    p.rect(30, 8, 4, 8, C.brass).rect(31, 5, 2, 3, '#f4f0e6').px(31, 4, C.fire);
    return p.done();
  },
}));
reg(def({
  id: 'desk', name: '책상', fw: 2, fh: 1, sw: 32, sh: 24, search: true,
  draw: () => {
    const p = new Painter(32, 24);
    woodBox(p, 0, 0, 32, 8);
    p.hline(2, 2, 28, W.lt);
    woodBox(p, 0, 8, 32, 14);
    p.box(3, 10, 10, 10, W.dk).box(19, 10, 10, 10, W.dk);
    p.rect(7, 14, 2, 2, C.brass).rect(23, 14, 2, 2, C.brass);
    p.rect(12, 2, 8, 5, C.paper).hline(13, 4, 5, C.ink);
    p.rect(24, 1, 3, 5, '#1d1526').px(25, 0, C.brass);
    p.hline(0, 22, 32, O);
    feet(p, 0, 22, 32);
    return p.done();
  },
}));
reg(def({
  id: 'dresser', name: '서랍장', fw: 2, fh: 1, sw: 32, sh: 28, search: true, wallHug: true,
  draw: () => {
    const p = new Painter(32, 28);
    woodBox(p, 0, 0, 32, 26, PW);
    for (let i = 0; i < 3; i++) {
      p.box(3, 3 + i * 7, 26, 6, PW.dk);
      p.rect(14, 5 + i * 7, 4, 2, C.brass);
    }
    p.hline(0, 26, 32, O);
    feet(p, 0, 26, 32);
    return p.done();
  },
}));
reg(def({
  id: 'nightstand', name: '협탁', fw: 1, fh: 1, sw: 16, sh: 22, search: true,
  draw: () => {
    const p = new Painter(16, 22);
    woodBox(p, 0, 4, 16, 16);
    p.box(3, 8, 10, 5, W.dk).rect(7, 10, 2, 1, C.brass);
    p.rect(5, 0, 6, 5, C.brass).rect(6, 1, 4, 3, C.brassDk);
    p.hline(0, 20, 16, O);
    feet(p, 0, 20, 16);
    return p.done();
  },
}));
reg(def({
  id: 'chest', name: '궤짝', fw: 2, fh: 1, sw: 32, sh: 24, search: true,
  draw: () => {
    const p = new Painter(32, 24);
    p.panel(0, 0, 32, 10, '#6b4226', O, '#8f5b37', '#4a2d18');
    p.panel(0, 9, 32, 13, '#6b4226', O, '#8f5b37', '#4a2d18');
    p.vline(6, 1, 20, C.metalDk).vline(25, 1, 20, C.metalDk);
    p.rect(14, 7, 4, 6, C.brass).px(15, 10, O).px(16, 10, O);
    p.hline(0, 22, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'toybox', name: '장난감 상자', fw: 2, fh: 1, sw: 32, sh: 22, search: true, interact: 'toybox',
  draw: () => {
    const p = new Painter(32, 22);
    p.panel(0, 0, 32, 10, '#3b6fc7', O, '#5f8fe0', '#2a4f93');
    p.panel(0, 9, 32, 12, '#e2b53c', O, '#f0cf6a', '#b78a24');
    p.rect(4, 12, 6, 6, '#c8433d').rect(13, 12, 6, 6, '#4f9d5a').rect(22, 12, 6, 6, '#8a5bb8');
    p.hline(0, 20, 32, O);
    return p.done();
  },
}));

// ---------- kitchen / bath ----------
reg(def({
  id: 'stove', name: '화덕', fw: 2, fh: 1, sw: 32, sh: 36, tall: true, wallHug: true,
  draw: () => {
    const p = new Painter(32, 36);
    p.rect(12, 0, 8, 12, '#2a2a33').box(12, 0, 8, 12, O);
    p.panel(0, 10, 32, 24, '#2f2f3a', O, '#4a4a58', '#1c1c24');
    p.rect(4, 13, 24, 3, '#4a4a58');
    p.box(4, 18, 10, 12, '#4a4a58').box(18, 18, 10, 12, '#4a4a58');
    p.rect(6, 22, 6, 6, C.fireDk).rect(7, 23, 4, 3, C.fire).px(8, 23, C.fireHi);
    p.rect(20, 24, 6, 3, '#4a4a58');
    p.hline(0, 34, 32, O);
    feet(p, 0, 34, 32);
    return p.done();
  },
}));
reg(def({
  id: 'counter', name: '조리대', fw: 2, fh: 1, sw: 32, sh: 28, search: true, wallHug: true,
  draw: () => {
    const p = new Painter(32, 28);
    p.panel(0, 0, 32, 8, C.tileA, O, '#f4f0e6', '#b9b2a2');
    woodBox(p, 0, 7, 32, 19, PW);
    p.box(3, 10, 11, 13, PW.dk).box(18, 10, 11, 13, PW.dk);
    p.rect(8, 15, 2, 2, C.metal).rect(23, 15, 2, 2, C.metal);
    p.rect(4, 2, 6, 4, '#8fb4c8').rect(20, 1, 3, 5, '#c9a24a');
    p.hline(0, 26, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'sink', name: '개수대', fw: 1, fh: 1, sw: 16, sh: 24, wallHug: true,
  draw: () => {
    const p = new Painter(16, 24);
    p.rect(7, 0, 2, 8, C.metal).rect(6, 0, 4, 2, C.metalLt);
    p.panel(0, 7, 16, 8, C.tileA, O, '#f4f0e6', '#b9b2a2');
    p.rect(3, 9, 10, 4, C.glass);
    p.panel(1, 14, 14, 9, C.plaster, O, C.plasterLt, C.plasterDk);
    return p.done();
  },
}));
reg(def({
  id: 'toilet', name: '변기', fw: 1, fh: 1, sw: 16, sh: 22, wallHug: true,
  draw: () => {
    const p = new Painter(16, 22);
    p.panel(3, 0, 10, 8, C.tileA, O, '#f4f0e6', '#b9b2a2');
    p.panel(2, 9, 12, 12, C.tileA, O, '#f4f0e6', '#b9b2a2');
    p.rect(5, 11, 6, 5, C.glass);
    return p.done();
  },
}));
reg(def({
  id: 'bathtub', name: '욕조', fw: 3, fh: 2, sw: 48, sh: 32, hide: { cap: 1, label: '욕조 안', verb: '욕조 안으로 숨는다' },
  draw: () => {
    const p = new Painter(48, 32);
    p.art(0, 0, [
      '....OOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOO....',
      '..OOWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWOO..',
      '.OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO.',
      'OWWWOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOWWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWOSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSOWWO',
      'OWWWOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      '.OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO.',
      '..OODDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDOO..',
      '....OOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOO....',
    ], { O, W: C.tileA, S: '#5a7f92', D: '#b9b2a2' });
    p.rect(4, 22, 3, 4, O).rect(41, 22, 3, 4, O);
    p.rect(20, 3, 3, 3, C.metal).rect(25, 3, 3, 3, C.metal);
    return p.done();
  },
}));
reg(def({
  id: 'tub', name: '빨래통', fw: 2, fh: 1, sw: 32, sh: 24, hide: { cap: 1, label: '빨래통 안', verb: '빨래 더미 속으로 숨는다' },
  draw: () => {
    const p = new Painter(32, 24);
    p.panel(0, 6, 32, 16, '#8a8a95', O, '#aab0ba', '#555a63');
    p.hline(0, 10, 32, '#555a63').hline(0, 17, 32, '#555a63');
    p.rect(3, 2, 26, 7, C.sheet).box(3, 2, 26, 7, C.sheetSh);
    p.rect(6, 0, 8, 4, C.fabricBlue).rect(18, 1, 7, 4, C.fabricRed);
    p.hline(0, 22, 32, O);
    return p.done();
  },
}));

// ---------- misc big ----------
reg(def({
  id: 'piano', name: '피아노', fw: 3, fh: 2, sw: 48, sh: 36, tall: true, interact: 'piano', wallHug: true,
  draw: () => {
    const p = new Painter(48, 36);
    p.panel(0, 0, 48, 24, '#1d1526', O, '#3a2c48', '#0e0a12');
    p.rect(3, 3, 42, 6, '#0e0a12');
    p.rect(2, 24, 44, 6, '#f4f0e6').box(2, 24, 44, 6, O);
    for (let i = 3; i < 46; i += 3) p.rect(i, 25, 1, 3, O);
    for (let i = 4; i < 46; i += 6) if ((i / 2) % 3 !== 0) p.rect(i, 25, 2, 3, O);
    p.rect(0, 30, 48, 3, '#1d1526');
    p.rect(2, 33, 3, 3, O).rect(43, 33, 3, 3, O);
    p.hline(0, 33, 48, O);
    return p.done();
  },
}));
reg(def({
  id: 'organ', name: '파이프 오르간', fw: 3, fh: 1, sw: 48, sh: 48, tall: true, interact: 'organ', wallHug: true,
  draw: () => {
    const p = new Painter(48, 48);
    const heights = [20, 26, 32, 36, 32, 26, 20];
    for (let i = 0; i < 7; i++) {
      const x = 3 + i * 6, h = heights[i];
      p.rect(x, 36 - h, 5, h, C.metal).box(x, 36 - h, 5, h, O).vline(x + 1, 37 - h, h - 2, C.metalLt);
    }
    woodBox(p, 0, 34, 48, 12, { base: '#4a3324', dk: '#33221a', lt: '#5f4230' });
    p.rect(4, 37, 40, 5, '#f4f0e6');
    for (let i = 5; i < 44; i += 3) p.rect(i, 37, 1, 3, O);
    p.hline(0, 46, 48, O);
    return p.done();
  },
}));
reg(def({
  id: 'fireplace', name: '벽난로', fw: 3, fh: 1, sw: 48, sh: 40, tall: true, wallHug: true,
  light: { r: 70, color: '#ffb060', flicker: true, ox: 24, oy: 30 },
  draw: () => {
    const p = new Painter(48, 40);
    p.panel(0, 0, 48, 40, C.stone, O, C.stoneLt, C.stoneDk);
    p.rect(0, 0, 48, 5, C.stoneLt).hline(0, 5, 48, C.stoneDk);
    p.rect(8, 10, 32, 26, '#1a1210').box(8, 10, 32, 26, O);
    p.rect(12, 30, 24, 4, '#3a2a22');
    p.rect(14, 22, 20, 10, C.fireDk).rect(17, 18, 14, 12, C.fire).rect(20, 15, 8, 10, C.fireHi);
    p.px(16, 20, C.fire).px(31, 19, C.fire).px(23, 12, C.fireDk);
    p.hline(0, 38, 48, O);
    return p.done();
  },
}));
reg(def({
  id: 'billiard', name: '당구대', fw: 4, fh: 2, sw: 64, sh: 32,
  draw: () => {
    const p = new Painter(64, 32);
    p.rect(4, 22, 4, 8, W.dk).rect(56, 22, 4, 8, W.dk).rect(4, 29, 4, 2, O).rect(56, 29, 4, 2, O);
    woodBox(p, 0, 2, 64, 22);
    p.rect(3, 5, 58, 16, '#2f7a4a').box(3, 5, 58, 16, '#1d5a32');
    p.rect(20, 10, 3, 3, '#f4f0e6').rect(30, 13, 3, 3, '#c8433d').rect(40, 9, 3, 3, '#e2b53c').rect(36, 15, 3, 3, '#1d1526');
    p.rect(8, 12, 30, 1, '#8f5b37');
    return p.done();
  },
}));
reg(def({
  id: 'boiler', name: '보일러', fw: 3, fh: 2, sw: 48, sh: 48, tall: true,
  draw: () => {
    const p = new Painter(48, 48);
    p.rect(6, 0, 4, 20, C.metalDk).rect(38, 0, 4, 14, C.metalDk).rect(6, 0, 36, 4, C.metalDk);
    p.art(4, 8, [
      '......OOOOOOOOOOOOOOOOOOOOOOOOOOOO......',
      '....OOMMMMMMMMMMMMMMMMMMMMMMMMMMMMOO....',
      '..OOMMLLMMMMMMMMMMMMMMMMMMMMMMMMMMMMOO..',
      '.OMMMLLMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMO.',
      'OMMMMLMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMO',
    ], { O, M: C.metal, L: C.metalLt });
    p.panel(4, 12, 40, 30, C.metal, O, C.metalLt, C.metalDk);
    for (let y = 16; y < 40; y += 6) p.hline(5, y, 38, C.metalDk);
    p.rect(18, 18, 12, 12, '#1a1210').box(18, 18, 12, 12, O);
    p.rect(21, 21, 6, 6, C.fireDk).rect(22, 22, 4, 3, C.fire);
    p.rect(34, 16, 6, 6, '#f4f0e6').box(34, 16, 6, 6, O).px(37, 18, C.fireDk);
    p.rect(8, 33, 3, 3, C.brass).rect(13, 33, 3, 3, C.brass);
    p.hline(0, 42, 48, O);
    p.rect(6, 42, 3, 5, O).rect(39, 42, 3, 5, O);
    return p.done();
  },
}));
reg(def({
  id: 'well', name: '우물', fw: 2, fh: 2, sw: 32, sh: 40, interact: 'well',
  draw: () => {
    const p = new Painter(32, 40);
    p.rect(3, 0, 3, 24, W.dk).rect(26, 0, 3, 24, W.dk);
    woodBox(p, 0, 0, 32, 5);
    p.rect(12, 8, 8, 3, W.dk).rect(15, 8, 2, 10, C.metalDk);
    p.art(0, 20, [
      '.....OOOOOOOOOOOOOOOOOOOOOO.....',
      '...OOSSSSSSSSSSSSSSSSSSSSSSOO...',
      '.OOSSSSOOOOOOOOOOOOOOOOOOSSSSOO.',
      'OSSSSOODDDDDDDDDDDDDDDDDDOOSSSSO',
      'OSSSOODDDDDDDDDDDDDDDDDDDDOOSSSO',
      'OSSSSOODDDDDDDDDDDDDDDDDDOOSSSSO',
      'OSSSSSSOOOOOOOOOOOOOOOOOOSSSSSSO',
      'OSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSO',
      'OSDSSSSDSSSSSDSSSSSSDSSSSSDSSSSO',
      'OSSSSSSSSSSSSSSSSSSSSSSSSSSSSSSO',
      '.OSSSSSSSSSSSSSSSSSSSSSSSSSSSSO.',
      '..OOSSSSSSSSSSSSSSSSSSSSSSSSOO..',
      '....OOOOOOOOOOOOOOOOOOOOOOOO....',
    ], { O, S: C.stone, D: C.stoneDk });
    return p.done();
  },
}));
reg(def({
  id: 'waterTank', name: '물탱크', fw: 2, fh: 2, sw: 32, sh: 48, tall: true,
  draw: () => {
    const p = new Painter(32, 48);
    p.panel(2, 4, 28, 36, C.metal, O, C.metalLt, C.metalDk);
    p.rect(0, 0, 32, 6, C.metalDk).box(0, 0, 32, 6, O);
    for (let y = 12; y < 40; y += 9) p.hline(3, y, 26, C.metalDk);
    p.rect(14, 40, 4, 8, C.metalDk).rect(4, 40, 3, 8, C.metalDk).rect(25, 40, 3, 8, C.metalDk);
    p.noise(3, 6, 26, 32, '#8a5a37', 0.07, 2);
    return p.done();
  },
}));
reg(def({
  id: 'nest', name: '괴물의 둥지', fw: 3, fh: 3, sw: 48, sh: 48, solid: false, interact: 'nest', flat: true,
  draw: () => {
    const p = new Painter(48, 48);
    p.art(0, 0, [
      '..........OOOOOOOOOOOOOOOOOOOOOOOOOOO...........',
      '.......OOORRRRRRRRRRRRRRRRRRRRRRRRRRROOO........',
      '.....OORRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRROO......',
      '...OORRRRRRRRRDDDDDDDDDDDDDDDDDDDRRRRRRRRROO....',
      '..ORRRRRRRRRDDDDDDDDDDDDDDDDDDDDDDDRRRRRRRRRO...',
      '.ORRRRRRRRDDDDDDDDDDDDDDDDDDDDDDDDDDDRRRRRRRRO..',
      'ORRRRRRRRDDDDDDDDDDDDDDDDDDDDDDDDDDDDDRRRRRRRRO.',
      'ORRRRRRRDDDDDDDDBBBDDDDDDDDDDBDDDDDDDDDRRRRRRRO.',
      'ORRRRRRRDDDDDDDBWWWBDDDDDDDDBBBDDDDDDDDRRRRRRRRO',
      'ORRRRRRRDDDDDDBWWWWWBDDDDDDDDBDDDDDDDDDRRRRRRRRO',
      'ORRRRRRRDDDDDDBWWWWWBDDDDDDDDDDDDDDDDDDRRRRRRRRO',
      'ORRRRRRRRDDDDDBWWBWWBDDDDDDDDDDDDDDDDDRRRRRRRRRO',
      'ORRRRRRRRDDDDDDBWWWBDDDDDDDBBBBBDDDDDDRRRRRRRRRO',
      '.ORRRRRRRRDDDDDDBBBDDDDDDDDDDDDDDDDDDRRRRRRRRRO.',
      '.ORRRRRRRRRDDDDDDDDDDDDDDDDDDDDDDDDDRRRRRRRRRRO.',
      '..ORRRRRRRRRRDDDDDDDDDDDDDDDDDDDDDDRRRRRRRRRRO..',
      '...OORRRRRRRRRRRDDDDDDDDDDDDDDDDRRRRRRRRRRRROO..',
      '.....OORRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRROO....',
      '.......OOORRRRRRRRRRRRRRRRRRRRRRRRRRRROOO.......',
      '..........OOOOOOOOOOOOOOOOOOOOOOOOOOO...........',
    ].map((r) => r.padEnd(48, '.')), { O, R: '#3a2a2a', D: '#1e1418', B: '#d9d6dc', W: '#f4f0e6' });
    p.noise(4, 2, 40, 16, '#5a3a3a', 0.15, 7);
    p.rect(8, 22, 10, 3, '#5a4a3a').rect(30, 20, 8, 3, '#5a4a3a');
    return p.done();
  },
}));

// ---------- small ----------
reg(def({
  id: 'plant', name: '화분', fw: 1, fh: 1, sw: 16, sh: 28,
  draw: () => {
    const p = new Painter(16, 28);
    p.art(0, 0, [
      '....OO..OO......',
      '...OLLOOLLO.....',
      '..OLLLLLLLLO.OO.',
      '.OLLLDLLLLDLOLLO',
      '.OLLLLLLLLLLLLLO',
      '..OLLLLDLLLLLLO.',
      '...OOLLLLLLOOO..',
      '.....OLLLLO.....',
      '......OLLO......',
      '.......OO.......',
    ], { O, L: C.leaf, D: C.leafDk });
    p.rect(6, 9, 4, 4, C.leafDk);
    p.panel(2, 12, 12, 5, C.pot, O, '#c47a56', C.potDk);
    p.panel(3, 16, 10, 11, C.pot, O, '#c47a56', C.potDk);
    return p.done();
  },
}));
reg(def({
  id: 'cagedLamp', name: '철망 전등', fw: 1, fh: 1, sw: 16, sh: 28, solid: false,
  light: { r: 60, color: '#ffd080', intensity: 0.45, powered: true, flicker: true, ox: 8, oy: 15 },
  draw: () => {
    const p = new Painter(16, 28);
    // A short wall bracket and protective cage; the corridor floor remains passable.
    p.panel(0, 2, 3, 13, C.metalDk, O, C.metal);
    p.rect(2, 4, 8, 2, C.metalDk).hline(3, 4, 6, C.metalLt).rect(7, 5, 2, 4, C.metal);
    p.panel(3, 8, 10, 15, C.metalDk, O, C.metalLt, C.metalDk);
    p.rect(5, 10, 6, 11, '#333945').rect(6, 11, 4, 8, '#7d817b');
    p.vline(4, 10, 11, C.metal).vline(8, 10, 11, C.metalDk).vline(11, 10, 11, C.metalDk);
    p.hline(4, 14, 8, C.metalDk).hline(4, 20, 8, C.metal);
    p.rect(5, 23, 6, 2, O).px(1, 4, C.metalLt).px(1, 12, C.metalLt);
    return p.done();
  },
}));
reg(def({
  id: 'lamp', name: '스탠드', fw: 1, fh: 1, sw: 16, sh: 30, light: { r: 60, color: '#ffd9a0', powered: true, ox: 8, oy: 6 },
  draw: () => {
    const p = new Painter(16, 30);
    p.art(0, 0, [
      '.....OOOOOO.....',
      '....OSSSSSSO....',
      '...OSSSSSSSSO...',
      '..OSSSSSSSSSSO..',
      '.OSSSSSSSSSSSSO.',
      '.OOOOOOOOOOOOOO.',
    ], { O, S: '#e6cfa0' });
    p.rect(7, 6, 2, 20, C.brassDk).vline(7, 6, 20, C.brass);
    p.rect(3, 25, 10, 3, C.brass).box(3, 25, 10, 3, O);
    return p.done();
  },
}));
reg(def({
  id: 'candelabra', name: '촛대', fw: 1, fh: 1, sw: 16, sh: 30, light: { r: 46, color: '#ffc070', flicker: true, ox: 8, oy: 4 },
  draw: () => {
    const p = new Painter(16, 30);
    p.rect(3, 4, 2, 5, '#f4f0e6').rect(7, 2, 2, 7, '#f4f0e6').rect(11, 4, 2, 5, '#f4f0e6');
    p.px(3, 2, C.fire).px(4, 3, C.fireHi).px(7, 0, C.fire).px(8, 1, C.fireHi).px(11, 2, C.fire).px(12, 3, C.fireHi);
    p.rect(2, 9, 4, 2, C.brass).rect(6, 9, 4, 2, C.brass).rect(10, 9, 4, 2, C.brass);
    p.rect(7, 11, 2, 15, C.brassDk).vline(7, 11, 15, C.brass);
    p.rect(4, 26, 8, 3, C.brass).box(4, 26, 8, 3, O);
    return p.done();
  },
}));
reg(def({
  id: 'crate', name: '나무 상자', fw: 1, fh: 1, sw: 16, sh: 18, search: true,
  draw: () => {
    const p = new Painter(16, 18);
    woodBox(p, 0, 2, 16, 16, PW);
    p.box(2, 4, 12, 12, PW.dk);
    p.hline(3, 9, 10, PW.dk).vline(8, 5, 10, PW.dk);
    return p.done();
  },
}));
reg(def({
  id: 'crateStack', name: '상자 더미', fw: 2, fh: 2, sw: 32, sh: 40, tall: true, hide: { cap: 1, label: '상자 뒤', verb: '상자 더미 뒤로 숨는다' },
  draw: () => {
    const p = new Painter(32, 40);
    woodBox(p, 0, 22, 16, 16, PW);
    woodBox(p, 16, 22, 16, 16, PW);
    woodBox(p, 8, 8, 16, 16, PW);
    p.box(10, 10, 12, 12, PW.dk).box(2, 24, 12, 12, PW.dk).box(18, 24, 12, 12, PW.dk);
    p.hline(11, 15, 10, PW.dk).hline(3, 29, 10, PW.dk).hline(19, 29, 10, PW.dk);
    p.hline(0, 38, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'barrel', name: '통', fw: 1, fh: 1, sw: 16, sh: 22, hide: { cap: 1, label: '통 안', verb: '통 안으로 숨는다' },
  draw: () => {
    const p = new Painter(16, 22);
    p.art(0, 0, [
      '....OOOOOOOO....',
      '..OODDDDDDDDOO..',
      '.OWWWWWWWWWWWWO.',
      'OWWMMMMMMMMMMWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWMMMMMMMMMMWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWMMMMMMMMMMWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWO',
      'OWWMMMMMMMMMMWWO',
      'OWWWWWWWWWWWWWWO',
      '.OWWWWWWWWWWWWO.',
      '..OODDDDDDDDOO..',
      '....OOOOOOOO....',
    ], { O, W: '#6b4226', M: C.metalDk, D: '#4a2d18' });
    p.vline(7, 3, 15, '#8f5b37');
    return p.done();
  },
}));
reg(def({
  id: 'sack', name: '자루', fw: 1, fh: 1, sw: 16, sh: 16,
  draw: () => {
    const p = new Painter(16, 16);
    p.art(0, 0, [
      '......OOO.......',
      '.....OSSSO......',
      '....OSSSSSO.....',
      '...OSSSSSSSO....',
      '..OSSSSSSSSSO...',
      '.OSSSSSSSSSSSO..',
      '.OSSSSDSSSSSSO..',
      '.OSSSSSSSSDSSO..',
      '.OSSSSSSSSSSSO..',
      '..OSSSSSSSSSO...',
      '...OOOOOOOOO....',
    ], { O, S: '#b8a070', D: '#9a8458' });
    return p.done();
  },
}));
reg(def({
  id: 'bucket', name: '양동이', fw: 1, fh: 1, sw: 16, sh: 14, solid: false,
  draw: () => {
    const p = new Painter(16, 14);
    p.panel(3, 4, 10, 9, C.metal, O, C.metalLt, C.metalDk);
    p.hline(3, 5, 10, C.metalLt);
    p.box(4, 0, 8, 5, C.metalDk);
    return p.done();
  },
}));
reg(def({
  id: 'rubble', name: '잔해', fw: 1, fh: 1, sw: 16, sh: 16,
  draw: () => {
    const p = new Painter(16, 16);
    p.rect(2, 8, 6, 5, C.stone).box(2, 8, 6, 5, O).rect(8, 5, 6, 7, C.stoneDk).box(8, 5, 6, 7, O).rect(5, 12, 7, 3, C.stone).box(5, 12, 7, 3, O);
    p.noise(0, 4, 16, 12, C.stoneDk, 0.1, 3);
    return p.done();
  },
}));
reg(def({
  id: 'coalPile', name: '석탄 더미', fw: 2, fh: 1, sw: 32, sh: 20,
  draw: () => {
    const p = new Painter(32, 20);
    p.art(0, 0, [
      '..........OOOOOOOOOO............',
      '.......OOOCCCCCCCCCCOOO.........',
      '.....OOCCCCCCCCCCCCCCCCOO.......',
      '...OOCCCCCCCLCCCCCCCCCCCCOO.....',
      '..OCCCCCCCCCCCCCCCCLCCCCCCCO....',
      '.OCCCCCLCCCCCCCCCCCCCCCCCCCCO...',
      'OCCCCCCCCCCCCCLCCCCCCCCCCCCCCO..',
      'OCCCCCCCCCCCCCCCCCCCCCCLCCCCCCO.',
      '.OOOOOOOOOOOOOOOOOOOOOOOOOOOOOO.',
    ].map((r) => r.padEnd(32, '.')), { O, C: '#1a1a22', L: '#3a3a48' });
    return p.done();
  },
}));
reg(def({
  id: 'iceBlock', name: '얼음', fw: 1, fh: 1, sw: 16, sh: 18,
  draw: () => {
    const p = new Painter(16, 18);
    p.panel(1, 3, 14, 14, '#a9d4e8', O, '#dff2fb', '#7fb0c8');
    p.px(4, 6, '#ffffff').px(5, 7, '#ffffff');
    return p.done();
  },
}));
reg(def({
  id: 'statue', name: '조각상', fw: 1, fh: 1, sw: 16, sh: 40, tall: true,
  draw: () => {
    const p = new Painter(16, 40);
    p.art(0, 0, [
      '.....OOOOOO.....',
      '....OSSSSSSO....',
      '...OSSSSSSSSO...',
      '...OSSDSSDSSO...',
      '...OSSSSSSSSO...',
      '....OSSSSSSO....',
      '.....OSSSSO.....',
      '...OOOSSSSOOO...',
      '..OSSSSSSSSSSO..',
      '.OSSSSSSSSSSSSO.',
      '.OSSSSSSSSSSSSO.',
      '.OSSSSSSSSSSSSO.',
      '.OSSSSSSSSSSSSO.',
      '..OSSSSSSSSSSO..',
      '...OOOOOOOOOO...',
    ], { O, S: '#b8b8c2', D: '#8a8a95' });
    p.panel(2, 15, 12, 24, C.stone, O, C.stoneLt, C.stoneDk);
    p.rect(0, 36, 16, 4, C.stoneDk).box(0, 36, 16, 4, O);
    return p.done();
  },
}));
reg(def({
  id: 'pillar', name: '기둥', fw: 1, fh: 1, sw: 16, sh: 40, tall: true,
  draw: () => {
    const p = new Painter(16, 40);
    p.panel(0, 0, 16, 5, C.stoneLt, O, undefined, C.stoneDk);
    p.panel(2, 4, 12, 32, C.stone, O, C.stoneLt, C.stoneDk);
    p.vline(5, 6, 28, C.stoneLt).vline(10, 6, 28, C.stoneDk);
    p.panel(0, 35, 16, 5, C.stoneLt, O, undefined, C.stoneDk);
    return p.done();
  },
}));
reg(def({
  id: 'clock', name: '괘종시계', fw: 1, fh: 1, sw: 16, sh: 40, tall: true, wallHug: true, interact: 'clock',
  draw: () => {
    const p = new Painter(16, 40);
    woodBox(p, 0, 0, 16, 40, { base: '#4a3324', dk: '#33221a', lt: '#5f4230' });
    p.rect(3, 3, 10, 10, '#f4f0e6').box(3, 3, 10, 10, O);
    p.vline(8, 5, 4, O).hline(8, 8, 3, O);
    p.rect(4, 15, 8, 20, '#1a1210');
    p.rect(7, 16, 2, 14, C.brass).rect(5, 28, 6, 4, C.brass);
    p.hline(0, 39, 16, O);
    return p.done();
  },
}));
reg(def({
  id: 'safe', name: '금고', fw: 1, fh: 1, sw: 16, sh: 22, interact: 'safe', wallHug: true,
  draw: () => {
    const p = new Painter(16, 22);
    p.panel(0, 0, 16, 22, '#4a4a58', O, '#6a6a7a', '#2f2f3a');
    p.box(3, 3, 10, 14, '#2f2f3a');
    p.rect(6, 7, 4, 4, C.metalLt).box(6, 7, 4, 4, O).px(8, 8, O);
    p.rect(4, 13, 6, 2, C.brass);
    return p.done();
  },
}));
reg(def({
  id: 'strongbox', name: '쇠상자', fw: 2, fh: 1, sw: 32, sh: 26, interact: 'safe',
  draw: () => {
    const p = new Painter(32, 26);
    p.panel(0, 0, 32, 12, '#4a4a58', O, '#6a6a7a', '#2f2f3a');
    p.panel(0, 11, 32, 14, '#4a4a58', O, '#6a6a7a', '#2f2f3a');
    p.vline(6, 1, 22, C.metalDk).vline(25, 1, 22, C.metalDk);
    p.rect(12, 8, 8, 8, C.metalLt).box(12, 8, 8, 8, O);
    p.rect(14, 10, 4, 4, C.brass).px(16, 12, O);
    return p.done();
  },
}));
reg(def({
  id: 'crib', name: '아기 침대', fw: 2, fh: 2, sw: 32, sh: 32,
  draw: () => {
    const p = new Painter(32, 32);
    woodBox(p, 0, 0, 32, 6, PW);
    p.rect(1, 6, 30, 18, C.sheet);
    for (let x = 2; x < 32; x += 4) p.vline(x, 6, 20, PW.dk);
    p.box(0, 5, 32, 22, O);
    p.rect(10, 12, 12, 8, C.fabricBlue);
    p.rect(2, 26, 3, 5, O).rect(27, 26, 3, 5, O);
    p.hline(0, 27, 32, O);
    return p.done();
  },
}));
reg(def({
  id: 'rockingHorse', name: '흔들 목마', fw: 1, fh: 1, sw: 16, sh: 24,
  draw: () => {
    const p = new Painter(16, 24);
    p.art(0, 0, [
      '..OOO...........',
      '.OHHHO..........',
      'OHHHHHOOOOOO....',
      'OHOHHHHHHHHHHO..',
      'OHHHHHHHHHHHHHO.',
      '.OOOHHHHHHHHHHO.',
      '...OHHOOOOOHHO..',
      '...OHHO...OHHO..',
      '...OHHO...OHHO..',
      '...OOOO...OOOO..',
      '.OOOOOOOOOOOOOOO',
      'OWWWWWWWWWWWWWWO',
      '.OOOOOOOOOOOOOO.',
    ], { O, H: '#c47a56', W: PW.base });
    return p.done();
  },
}));
reg(def({
  id: 'doll', name: '인형', fw: 1, fh: 1, sw: 16, sh: 18, solid: false,
  draw: () => {
    const p = new Painter(16, 18);
    p.art(0, 0, [
      '.....OOOOOO.....',
      '....OHHHHHHO....',
      '...OHHHHHHHHO...',
      '...OHSSSSSSHO...',
      '...OSSDSSDSSO...',
      '...OSSSSSSSSO...',
      '....OSSSSSSO....',
      '.....OOOOOO.....',
      '....ODDDDDDO....',
      '...ODDDDDDDDO...',
      '...ODDDDDDDDO...',
      '..ODDDDDDDDDDO..',
      '..OOOOOOOOOOOO..',
    ], { O, H: '#e9c96f', S: '#f7d9b9', D: '#e07aa8' });
    return p.done();
  },
}));
reg(def({
  id: 'mannequin', name: '마네킹', fw: 1, fh: 1, sw: 16, sh: 36, tall: true,
  draw: () => {
    const p = new Painter(16, 36);
    p.art(0, 0, [
      '.....OOOOOO.....',
      '....OPPPPPPO....',
      '....OPPPPPPO....',
      '.....OPPPPO.....',
      '......OPPO......',
      '...OOOOPPOOOO...',
      '..OPPPPPPPPPPO..',
      '..OPPPPPPPPPPO..',
      '..OPPPPPPPPPPO..',
      '..OPPPPPPPPPPO..',
      '...OPPPPPPPPO...',
      '...OPPPPPPPPO...',
      '....OPPPPPPO....',
      '....OPPPPPPO....',
      '.....OPPPPO.....',
      '......OPPO......',
      '......OPPO......',
    ], { O, P: '#d7c0a0' });
    p.rect(7, 17, 2, 14, C.metalDk);
    p.rect(3, 31, 10, 4, C.metal).box(3, 31, 10, 4, O);
    return p.done();
  },
}));
reg(def({
  id: 'telescope', name: '망원경', fw: 1, fh: 1, sw: 16, sh: 30, interact: 'telescope',
  draw: () => {
    const p = new Painter(16, 30);
    p.art(0, 0, [
      '............OOO.',
      '..........OOMMMO',
      '........OOMMMMO.',
      '......OOMMMMOO..',
      '....OOMMMMOO....',
      '..OOMMMMOO......',
      '.OMMMMOO........',
      '.OMMOO..........',
      '..OO............',
    ], { O, M: C.brass });
    p.rect(7, 9, 2, 16, C.metalDk);
    p.rect(2, 24, 12, 2, C.metalDk).rect(1, 26, 4, 3, O).rect(11, 26, 4, 3, O);
    return p.done();
  },
}));
reg(def({
  id: 'globe', name: '지구본', fw: 1, fh: 1, sw: 16, sh: 24,
  draw: () => {
    const p = new Painter(16, 24);
    p.art(0, 0, [
      '.....OOOOOO.....',
      '...OOBBBBBBOO...',
      '..OBBGGBBBBBBO..',
      '.OBBGGGGBBBBGBO.',
      '.OBBBGGGBBBGGBO.',
      '.OBBBBBBBGGGGBO.',
      '.OBBBBBBGGGBBBO.',
      '..OBBBBBBBBBBO..',
      '...OOBBBBBBOO...',
      '.....OOOOOO.....',
    ], { O, B: '#3b6fc7', G: '#4f9d5a' });
    p.rect(7, 10, 2, 10, C.brassDk);
    p.rect(4, 20, 8, 3, C.brass).box(4, 20, 8, 3, O);
    return p.done();
  },
}));
reg(def({
  id: 'mirrorStand', name: '전신 거울', fw: 1, fh: 1, sw: 16, sh: 36, tall: true, interact: 'mirror',
  draw: () => {
    const p = new Painter(16, 36);
    p.panel(2, 0, 12, 30, C.brass, O, '#e0c070', C.brassDk);
    p.rect(4, 2, 8, 26, '#8fb4c8');
    p.vline(5, 3, 24, '#c4e0ee').px(9, 8, '#c4e0ee');
    p.rect(6, 30, 4, 3, C.brassDk);
    p.rect(3, 33, 10, 3, C.brass).box(3, 33, 10, 3, O);
    return p.done();
  },
}));
reg(def({
  id: 'sewingMachine', name: '재봉틀', fw: 2, fh: 1, sw: 32, sh: 28, search: true,
  draw: () => {
    const p = new Painter(32, 28);
    p.rect(8, 0, 16, 6, '#1d1526').box(8, 0, 16, 6, O);
    p.rect(20, 4, 4, 8, '#1d1526').rect(6, 6, 6, 6, '#1d1526');
    p.px(22, 12, C.metalLt);
    woodBox(p, 0, 12, 32, 12, PW);
    p.rect(2, 24, 3, 4, C.metalDk).rect(27, 24, 3, 4, C.metalDk);
    return p.done();
  },
}));
reg(def({
  id: 'lectern', name: '설교대', fw: 1, fh: 1, sw: 16, sh: 28,
  draw: () => {
    const p = new Painter(16, 28);
    woodBox(p, 0, 0, 16, 8, { base: '#4a3324', dk: '#33221a', lt: '#5f4230' });
    p.rect(3, 1, 10, 5, C.paper);
    p.rect(6, 8, 4, 16, '#33221a');
    p.rect(2, 24, 12, 3, '#4a3324').box(2, 24, 12, 3, O);
    return p.done();
  },
}));
reg(def({
  id: 'altar', name: '제단', fw: 3, fh: 1, sw: 48, sh: 36, wallHug: true, light: { r: 40, color: '#ffc070', flicker: true, ox: 24, oy: 6 },
  draw: () => {
    const p = new Painter(48, 36);
    p.rect(6, 4, 2, 6, '#f4f0e6').rect(40, 4, 2, 6, '#f4f0e6').px(6, 2, C.fire).px(40, 2, C.fire).px(7, 3, C.fireHi).px(41, 3, C.fireHi);
    p.rect(5, 10, 4, 2, C.brass).rect(39, 10, 4, 2, C.brass);
    p.rect(22, 2, 4, 10, C.brass).rect(20, 5, 8, 2, C.brass);
    p.panel(0, 12, 48, 22, C.stone, O, C.stoneLt, C.stoneDk);
    p.rect(2, 13, 44, 8, '#8a2c2f').hline(2, 20, 44, C.carpetGold);
    p.hline(0, 34, 48, O);
    return p.done();
  },
}));
reg(def({
  id: 'coffin', name: '관', fw: 2, fh: 1, sw: 32, sh: 28, hide: { cap: 1, label: '관 안', verb: '관 속으로 숨는다' },
  draw: () => {
    const p = new Painter(32, 28);
    p.art(0, 0, [
      '....OOOOOOOOOOOOOOOOOOOOOOOO....',
      '..OOWWWWWWWWWWWWWWWWWWWWWWWWOO..',
      'OOWWWWWWWWWWWWWWWWWWWWWWWWWWWWOO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWO',
      'OOWWWWWWWWWWWWWWWWWWWWWWWWWWWWOO',
      '..OODDDDDDDDDDDDDDDDDDDDDDDDOO..',
      '....OOOOOOOOOOOOOOOOOOOOOOOO....',
    ], { O, W: '#4a3324', D: '#33221a' });
    p.hline(2, 4, 28, '#5f4230');
    p.box(6, 6, 20, 16, '#33221a');
    p.rect(14, 10, 4, 8, C.brass).rect(12, 12, 8, 2, C.brass);
    return p.done();
  },
}));
reg(def({
  id: 'bookPile', name: '책 더미', fw: 1, fh: 1, sw: 16, sh: 12, solid: false,
  draw: () => {
    const p = new Painter(16, 12);
    p.rect(2, 8, 12, 3, '#a33a3a').box(2, 8, 12, 3, O);
    p.rect(3, 5, 11, 3, '#3a5a9a').box(3, 5, 11, 3, O);
    p.rect(1, 2, 12, 3, '#4f9d5a').box(1, 2, 12, 3, O);
    return p.done();
  },
}));
reg(def({
  id: 'bones', name: '뼈', fw: 1, fh: 1, sw: 16, sh: 12, solid: false, flat: true,
  draw: () => {
    const p = new Painter(16, 12);
    p.rect(2, 4, 10, 2, '#e8e2d2').rect(1, 3, 2, 4, '#e8e2d2').rect(11, 3, 2, 4, '#e8e2d2');
    p.rect(6, 7, 5, 4, '#e8e2d2').px(7, 8, O).px(9, 8, O);
    return p.done();
  },
}));

// ---------- flat ----------
reg(def({
  id: 'rug', name: '양탄자', fw: 3, fh: 2, sw: 48, sh: 32, flat: true, solid: false,
  draw: () => {
    const p = new Painter(48, 32);
    p.rect(0, 0, 48, 32, C.carpetRed).box(0, 0, 48, 32, C.carpetRedDk);
    p.box(3, 3, 42, 26, C.carpetGold).box(6, 6, 36, 20, C.carpetRedDk);
    p.rect(20, 12, 8, 8, C.carpetGold).rect(22, 14, 4, 4, C.carpetRedDk);
    return p.done();
  },
}));
reg(def({
  id: 'rugBlue', name: '양탄자', fw: 3, fh: 2, sw: 48, sh: 32, flat: true, solid: false,
  draw: () => {
    const p = new Painter(48, 32);
    p.rect(0, 0, 48, 32, C.carpetBlue).box(0, 0, 48, 32, C.carpetBlueDk);
    p.box(3, 3, 42, 26, '#a9b4d8').box(6, 6, 36, 20, C.carpetBlueDk);
    p.rect(20, 12, 8, 8, '#a9b4d8');
    return p.done();
  },
}));
reg(def({
  id: 'rugRound', name: '둥근 양탄자', fw: 2, fh: 2, sw: 32, sh: 32, flat: true, solid: false,
  draw: () => {
    const p = new Painter(32, 32);
    p.art(0, 0, [
      '..........OOOOOOOOOOOO..........',
      '.......OOORRRRRRRRRRRROOO.......',
      '.....OORRRRRRRRRRRRRRRRRROO.....',
      '....ORRRRGGGGGGGGGGGGGGRRRRO....',
      '...ORRRRGRRRRRRRRRRRRRRGRRRRO...',
      '..ORRRRGRRRRRRRRRRRRRRRRGRRRRO..',
      '.ORRRRGRRRRRRRRRRRRRRRRRRGRRRRO.',
      '.ORRRRGRRRRRRRRRRRRRRRRRRGRRRRO.',
      'ORRRRGRRRRRRRRGGGGRRRRRRRRGRRRRO',
      'ORRRRGRRRRRRRGRRRRGRRRRRRRGRRRRO',
      'ORRRRGRRRRRRRGRRRRGRRRRRRRGRRRRO',
      'ORRRRGRRRRRRRRGGGGRRRRRRRRGRRRRO',
      '.ORRRRGRRRRRRRRRRRRRRRRRRGRRRRO.',
      '.ORRRRGRRRRRRRRRRRRRRRRRRGRRRRO.',
      '..ORRRRGRRRRRRRRRRRRRRRRGRRRRO..',
      '...ORRRRGRRRRRRRRRRRRRRGRRRRO...',
      '....ORRRRGGGGGGGGGGGGGGRRRRO....',
      '.....OORRRRRRRRRRRRRRRRRROO.....',
      '.......OOORRRRRRRRRRRROOO.......',
      '..........OOOOOOOOOOOO..........',
    ], { O: C.carpetGreenDk, R: C.carpetGreen, G: C.carpetGold });
    return p.done();
  },
}));
reg(def({
  id: 'puddle', name: '물웅덩이', fw: 1, fh: 1, sw: 16, sh: 12, flat: true, solid: false,
  draw: () => {
    const p = new Painter(16, 12);
    p.art(0, 0, ['....WWWWWW......', '..WWWWWWWWWW....', '.WWWWLWWWWWWW...', 'WWWWWWWWWWWWWW..', '.WWWWWWWWWLWWW..', '...WWWWWWWWW....', '.....WWWWW......'], { W: '#2f3f5a', L: '#4f6f8a' });
    return p.done();
  },
}));

// ---------- door-ish interactables handled elsewhere; fusebox is decor ----------

export function propAt(kind: string): PropKind {
  const k = PROPS[kind];
  if (!k) throw new Error('unknown prop ' + kind);
  return k;
}

// ================= wall decor =================
const decorCache = new Map<string, HTMLCanvasElement>();
export function decorSprite(kind: DecorKind, variant = 0): HTMLCanvasElement {
  const key = kind + ':' + variant;
  let c = decorCache.get(key);
  if (c) return c;
  c = finishMaterial(ornament(kind, variant) ?? drawDecor(kind, variant));
  decorCache.set(key, c);
  return c;
}
export const DECOR_W: Record<DecorKind, number> = {
  painting: 1, paintingBig: 2, mirror: 1, window: 1, windowBig: 2, sconce: 1, fusebox: 1, hook: 1, shelf: 1, clockWall: 1, antlers: 1, crack: 1, poster: 1, cross: 1, vent: 1, plaque: 1, crawlspace: 2,
};

function drawDecor(kind: DecorKind, v: number): HTMLCanvasElement {
  switch (kind) {
    case 'painting': {
      const p = new Painter(16, 16);
      p.panel(2, 2, 12, 11, C.brass, O, '#e0c070', C.brassDk);
      const scenes = [
        () => p.rect(4, 4, 8, 7, '#3a5a9a').rect(4, 8, 8, 3, '#4f9d5a').px(6, 5, '#f4f0e6'),
        () => p.rect(4, 4, 8, 7, '#2a2a33').rect(6, 6, 4, 4, '#f7d9b9').px(7, 7, O).px(8, 7, O),
        () => p.rect(4, 4, 8, 7, '#8a2c2f').rect(5, 5, 6, 5, '#c9a24a'),
        () => p.rect(4, 4, 8, 7, '#1a2340').px(9, 5, '#dfe6ff').rect(4, 9, 8, 2, '#2f3f5a'),
        () => p.rect(4, 4, 8, 7, '#4a3324').rect(6, 5, 4, 5, '#d7c0a0').px(7, 6, O).px(8, 6, O),
      ];
      scenes[v % scenes.length]();
      return p.done();
    }
    case 'paintingBig': {
      const p = new Painter(32, 16);
      p.panel(1, 1, 30, 14, C.brass, O, '#e0c070', C.brassDk);
      if (v % 2 === 0) {
        p.rect(3, 3, 26, 10, '#1a2340').rect(3, 9, 26, 4, '#2f3f5a').rect(10, 4, 12, 6, '#3a3a48').rect(14, 2, 4, 4, '#3a3a48');
        p.px(24, 4, '#dfe6ff');
      } else {
        p.rect(3, 3, 26, 10, '#2a2a33');
        p.rect(8, 5, 6, 8, '#f7d9b9').rect(18, 5, 6, 8, '#f7d9b9').px(9, 7, O).px(12, 7, O).px(19, 7, O).px(22, 7, O);
      }
      return p.done();
    }
    case 'mirror': {
      const p = new Painter(16, 16);
      p.panel(3, 1, 10, 13, C.brass, O, '#e0c070', C.brassDk);
      p.rect(5, 3, 6, 9, '#8fb4c8').vline(6, 4, 7, '#c4e0ee');
      return p.done();
    }
    case 'window': {
      const p = new Painter(16, 16);
      p.rect(2, 1, 12, 13, C.night).box(2, 1, 12, 13, O);
      p.px(10, 3, C.moon).px(11, 3, C.moon).px(10, 4, C.moon).px(11, 4, C.moon).px(5, 6, '#9aa4c8');
      p.vline(8, 2, 11, '#3a3348').hline(3, 7, 10, '#3a3348');
      p.hline(1, 14, 14, C.panelLt).hline(1, 15, 14, C.panelDk);
      return p.done();
    }
    case 'windowBig': {
      const p = new Painter(32, 16);
      p.rect(2, 1, 28, 13, C.night).box(2, 1, 28, 13, O);
      p.rect(20, 3, 3, 3, C.moon).px(8, 5, '#9aa4c8').px(25, 8, '#9aa4c8');
      p.vline(10, 2, 11, '#3a3348').vline(21, 2, 11, '#3a3348').hline(3, 7, 26, '#3a3348');
      p.hline(1, 14, 30, C.panelLt).hline(1, 15, 30, C.panelDk);
      return p.done();
    }
    case 'sconce': {
      const p = new Painter(16, 16);
      p.rect(7, 6, 2, 5, '#f4f0e6').px(7, 4, C.fire).px(8, 5, C.fireHi).px(7, 3, C.fireDk);
      p.rect(5, 11, 6, 2, C.brass).rect(7, 13, 2, 2, C.brassDk);
      return p.done();
    }
    case 'fusebox': {
      const p = new Painter(16, 16);
      p.panel(2, 1, 12, 14, '#4a4a58', O, '#6a6a7a', '#2f2f3a');
      p.rect(4, 3, 8, 6, '#2f2f3a');
      p.rect(5, 4, 2, 4, '#c8433d').rect(9, 4, 2, 4, '#4f9d5a');
      p.rect(6, 11, 4, 2, C.brass);
      return p.done();
    }
    case 'hook': {
      const p = new Painter(16, 16);
      p.rect(5, 4, 6, 2, C.brassDk).px(7, 6, C.brass).px(8, 6, C.brass).px(7, 7, C.brass);
      return p.done();
    }
    case 'shelf': {
      const p = new Painter(16, 16);
      p.rect(1, 9, 14, 2, W.base).hline(1, 9, 14, W.lt);
      p.rect(3, 4, 3, 5, '#8fb4c8').rect(7, 5, 3, 4, '#c9a24a').rect(11, 3, 3, 6, '#4f9d5a');
      return p.done();
    }
    case 'clockWall': {
      const p = new Painter(16, 16);
      p.rect(4, 3, 8, 8, '#f4f0e6').box(3, 2, 10, 10, C.brassDk).px(8, 5, O).px(8, 6, O).px(9, 6, O);
      return p.done();
    }
    case 'antlers': {
      const p = new Painter(16, 16);
      p.rect(6, 8, 4, 4, '#8f5b37').box(5, 7, 6, 6, O);
      p.rect(3, 3, 1, 5, '#e8e2d2').rect(4, 5, 2, 1, '#e8e2d2').rect(2, 2, 1, 2, '#e8e2d2');
      p.rect(12, 3, 1, 5, '#e8e2d2').rect(10, 5, 2, 1, '#e8e2d2').rect(13, 2, 1, 2, '#e8e2d2');
      return p.done();
    }
    case 'crack': {
      const p = new Painter(16, 16);
      p.px(4, 2, O).px(5, 3, O).px(5, 4, O).px(6, 5, O).px(7, 6, O).px(7, 7, O).px(6, 8, O).px(8, 8, O).px(8, 9, O).px(9, 10, O).px(9, 11, O);
      return p.done();
    }
    case 'poster': {
      const p = new Painter(16, 16);
      p.rect(3, 2, 10, 11, C.paper).box(3, 2, 10, 11, C.paperDk);
      p.hline(5, 5, 6, C.ink).hline(5, 7, 5, C.ink).hline(5, 9, 6, C.ink);
      p.px(8, 1, C.metalDk);
      return p.done();
    }
    case 'cross': {
      const p = new Painter(16, 16);
      p.rect(7, 1, 2, 12, '#4a3324').rect(4, 4, 8, 2, '#4a3324');
      return p.done();
    }
    case 'vent': {
      const p = new Painter(16, 16);
      p.panel(3, 4, 10, 8, C.metalDk, O, C.metal);
      for (let y = 6; y < 11; y += 2) p.hline(5, y, 6, '#1a1a22');
      return p.done();
    }
    case 'crawlspace': {
      const p = new Painter(32, 28);
      p.panel(2, 2, 28, 23, '#292830', O, '#69616a', '#16141c');
      p.rect(5, 5, 22, 18, '#09080f');
      for (let x = 7; x < 26; x += 5) {
        p.rect(x, 5, 2, 16, '#45414b').hline(x, 5, 2, '#817778');
        p.px(x, 17, '#94705b');
      }
      p.hline(5, 11, 22, '#514953').hline(4, 22, 24, '#8b7970');
      p.rect(13, 15, 10, 8, '#0b090f'); // bars have been bent inward
      for (let i = 0; i < 3; i++) {
        for (let k = 0; k < 8; k++) p.px(22 + i * 2 - Math.floor(k * 0.7), 1 + k, '#b5a296');
        for (let k = 0; k < 6; k++) p.px(8 + i * 3 - Math.floor(k * 0.4), 22 + k, '#8e7569');
      }
      for (const [x,y] of [[3,3],[28,3],[3,23],[28,23]]) p.px(x,y,'#a09485');
      return p.done();
    }
    case 'plaque': {
      const p = new Painter(16, 16);
      p.panel(2, 4, 12, 7, C.brass, O, '#e0c070', C.brassDk);
      p.hline(4, 6, 8, C.brassDk).hline(4, 8, 6, C.brassDk);
      return p.done();
    }
  }
}
